import { useEffect, useState } from 'react';

import { useFeatureFlags } from './context';
import { IS_PROD } from './evaluate';
import { type FlagReadScope, registerFlagRead } from './reads';
import { type FlagKey } from './registry';

/**
 * Whether a feature flag is on: the only way a component reads one.
 *
 * Outside prod every flag is on (a localStorage override aside, see
 * evaluate.ts). In prod a flag is on only when its row says so; no row or a
 * failed read means the old code.
 *
 * The key and the value are read once per mount, so a flip applies on the next
 * navigation, never in the middle of a form.
 *
 *   const showFollowUp = useFeatureFlag('job-details.follow-up-flow');
 *   return showFollowUp ? <FollowUpJobFlow /> : <CurrentFooter />;
 *
 * Outside prod the floating legend lists the flag while the component is on screen, with the
 * value read here (reads.ts); wrap the UI the flag owns in <Flagged> to draw its border. A flag
 * that decides only part of what the component does passes `applies` for when that part is on
 * screen: the field layout applies on phones and tablets, so on desktop the flag is read but
 * not listed. `scope: 'page'` is for a flag that changes the whole screen; the legend frames
 * the viewport in its colour instead of a region. In prod nothing is registered.
 */
export function useFeatureFlag(
  key: FlagKey,
  { applies = true, scope = 'region' }: { applies?: boolean; scope?: FlagReadScope } = {},
): boolean {
  const { isEnabled } = useFeatureFlags();
  const [read] = useState(() => ({ key, value: isEnabled(key) }));
  useEffect(
    () => (!IS_PROD && applies ? registerFlagRead(read.key, read.value, scope) : undefined),
    [read, applies, scope],
  );
  return read.value;
}
